import ForumOutlined from '@mui/icons-material/ForumOutlined'
import ReadMore from '@mui/icons-material/ReadMore'
import { Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'

type DrawerToggleButtonsProps = {
  /** Active comment count for the badge; owned and fetched by the parent
   * (ValidationOverlayContent) so the badge and the comments drawer's
   * invalidation share one source of truth. Undefined while loading or on
   * fetch failure, which simply hides the count. */
  commentsCount?: number
  commentsCountIsPending?: boolean
  commentsOverlay: OverlayState
  fieldDetailsOverlay: OverlayState
  showFieldDetails: boolean
  disabled?: boolean
}

/**
 * The Comments and Field Details panels share the single right-hand drawer
 * slot, so they are mutually exclusive: opening one closes the other.
 */
export default function DrawerToggleButtons({
  commentsCount,
  commentsCountIsPending = false,
  commentsOverlay,
  fieldDetailsOverlay,
  showFieldDetails,
  disabled = false,
}: DrawerToggleButtonsProps) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip
        title={disabled ? '' : 'Comments'}
        arrow
        disableInteractive
        enterDelay={1000}
      >
        <IconButton
          size="small"
          disabled={disabled}
          aria-label="Comments"
          color={commentsOverlay.isOpen ? 'primary' : 'default'}
          onClick={() => {
            // Close Field Details on every view, not just the ones that show
            // its button. Both panels remember whether they were open, so
            // skipping this on other views would let the user come back to
            // validation with both open and stacked on top of each other.
            fieldDetailsOverlay.close()
            commentsOverlay.toggle()
          }}
          onMouseDown={(e) => e.preventDefault()}
          sx={{ borderRadius: 5, px: 1 }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ForumOutlined />
            <Typography
              variant="caption"
              sx={{
                minWidth: '1ch', // To keep it from jumping around when loading
                opacity: commentsCountIsPending ? 0 : 1,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              {commentsCount}
            </Typography>
          </Stack>
        </IconButton>
      </Tooltip>

      {showFieldDetails && (
        <>
          <Divider orientation="vertical" sx={{ height: 20 }} />

          <Tooltip
            title={disabled ? '' : 'Field Details'}
            arrow
            disableInteractive
            enterDelay={1000}
          >
            <IconButton
              size="small"
              disabled={disabled}
              aria-label="Field Details"
              color={fieldDetailsOverlay.isOpen ? 'primary' : 'default'}
              onClick={() => {
                commentsOverlay.close()
                fieldDetailsOverlay.toggle()
              }}
              onMouseDown={(e) => e.preventDefault()}
            >
              <ReadMore
                sx={{
                  transform: `rotate(${
                    fieldDetailsOverlay.isOpen ? 0 : '180deg'
                  })`,
                }}
              />
            </IconButton>
          </Tooltip>
        </>
      )}
    </Stack>
  )
}
